import moment from 'moment';

export class AutoArchiveService {
	constructor(Cfg, $filter) {
		'ngInject';
		/**
		 * The Api Service
		 * @type {Cfg}
		 */
		this.Cfg = Cfg;
		this.shDateFormat = $filter('shDate');
	}

	getArchiveDropdown() {
		const isArchiveEnabled = this.Cfg.readAsBool('PostArchive.enable_archive');
		const isDestroyEnabled = this.Cfg.readAsBool('PostArchive.enable_destroy');
		const allowedPeriodsString = this.Cfg.read('PostArchive.allowed_periods');
		if (!allowedPeriodsString) {
			return [];
		}
		const allowedTimePeriods = allowedPeriodsString.split('\n');
		const formattedTimePeriods = allowedTimePeriods.map(period => period.trim());
		const neverOption = [];
		const archiveOptions = [];
		const destroyOptions = [];
		formattedTimePeriods.forEach(timePeriod => {
			if (timePeriod === 'never') {
				if (isArchiveEnabled && isDestroyEnabled) {
					neverOption.push({ id: 'never', name: 'Never archive or destroy post' });
				} else if (isArchiveEnabled) {
					neverOption.push({ id: 'never', name: 'Never archive post' });
				} else {
					neverOption.push({ id: 'never', name: 'Never destroy post' });
				}
			} else {
				if (isArchiveEnabled) {
					archiveOptions.push({
						id: `archive ${timePeriod}`,
						name: `Auto archive after ${timePeriod}`,
					});
				}
				if (isDestroyEnabled) {
					destroyOptions.push({
						id: `destroy ${timePeriod}`,
						name: `Auto destroy after ${timePeriod}`,
					});
				}
			}
		});

		const dropDownOptions = [...neverOption, ...archiveOptions, ...destroyOptions];

		//Check if default period is one of the options and add it if it isn't
		const defaultPeriod = this.Cfg.read('PostArchive.default_period');
		if (defaultPeriod) {
			const indexOfDefaultPeriod = dropDownOptions.findIndex(option => {
				return option.id === defaultPeriod;
			});
			if (indexOfDefaultPeriod === -1) {
				const archiveTimePeriod = defaultPeriod.substr(defaultPeriod.indexOf(' ') + 1);
				if (defaultPeriod.includes('archive')) {
					dropDownOptions.unshift({
						id: defaultPeriod,
						name: `Auto archive after ${archiveTimePeriod}`,
					});
				} else if (defaultPeriod.includes('destroy')) {
					dropDownOptions.unshift({
						id: defaultPeriod,
						name: `Auto destroy after ${archiveTimePeriod}`,
					});
				}
			}
		}

		return dropDownOptions;
	}

	getArchivePresetFineTunerDropdown() {
		const allowedPeriodsString = this.Cfg.read('PostArchive.allowed_periods');
		let archivePresetOptions = [];
		if (allowedPeriodsString) {
			const allowedTimePeriods = allowedPeriodsString.split('\n');
			const formattedTimePeriods = allowedTimePeriods.map(period => period.trim());
			archivePresetOptions = [{ id: '', name: 'Select date range...' }];
			formattedTimePeriods.forEach(timePeriod => {
				if (timePeriod !== 'never') {
					archivePresetOptions.push({ id: timePeriod, name: timePeriod });
				}
			});
		}
		return archivePresetOptions;
	}

	getDateAndActionFromArchiveSelection(selection) {
		let action;
		let archiveAt;
		if (selection === 'never') {
			action = selection;
		} else {
			action = selection.substr(0, selection.indexOf(' '));
			const archiveTimePeriod = selection.substr(selection.indexOf(' ') + 1);

			const archiveNumberString = archiveTimePeriod.substr(0, archiveTimePeriod.indexOf(' '));
			const archiveUnit = archiveTimePeriod.substr(archiveTimePeriod.indexOf(' ') + 1);

			const archiveNumber = parseInt(archiveNumberString, 10);

			archiveAt = moment().add(archiveNumber, archiveUnit);
		}
		return { archive_action: action, archive_at: archiveAt };
	}

	/**
	 * Extract the archive action and archive date preset to use based on the search input
	 * @param {Object} search
	 * @property {String} archiveAction  A named archive action
	 * @property {String} archiveSinceDate  archive date since
	 * @property {String} archiveBeforeDate  archive date before
	 * @returns {Object}
	 */
	getArchiveDataFromSearch(search) {
		const archiveData = {
			archiveAction: undefined,
			archiveSinceDate: undefined,
			archiveBeforeDate: undefined,
		};
		if (search.archiveAction === undefined || search.archiveDatePreset === undefined) {
			return archiveData;
		}
		const archiveNumberString = search.archiveDatePreset.substr(0, search.archiveDatePreset.indexOf(' '));
		const archiveNumber = parseInt(archiveNumberString, 10);
		const archiveUnit = search.archiveDatePreset.substr(search.archiveDatePreset.indexOf(' ') + 1);

		switch (search.archiveAction) {
			case 'archive past':
				archiveData.archiveAction = 'archive';
				archiveData.archiveBeforeDate = moment().toISOString();
				archiveData.archiveSinceDate = moment().startOf('day').subtract(archiveNumber, archiveUnit).toISOString();
				break;
			case 'active_and_archived':
				archiveData.archiveAction = 'active_and_archived';
				archiveData.archiveBeforeDate = moment().toISOString();
				archiveData.archiveSinceDate = moment().startOf('day').subtract(archiveNumber, archiveUnit).toISOString();
				break;
			case 'archive future':
				archiveData.archiveAction = 'archive';
				archiveData.archiveBeforeDate = moment().endOf('day').add(archiveNumber, archiveUnit).toISOString();
				archiveData.archiveSinceDate = moment().toISOString();
				break;
			default:
				archiveData.archiveAction = search.archiveAction;
				archiveData.archiveBeforeDate = moment().endOf('day').add(archiveNumber, archiveUnit).toISOString();
				archiveData.archiveSinceDate = moment().toISOString();
		}

		return archiveData;
	}

	getArchiveSelectionLabel(archive_action, archive_at) {
		const archiveDateShort = this.shDateFormat(archive_at, 'short');

		let archiveActionPastOrPresent;
		const currentDate = moment();
		const isFuture = moment(archive_at).isAfter(currentDate);
		if (isFuture) {
			if (archive_action === 'archive') {
				archiveActionPastOrPresent = 'Archive';
			} else {
				archiveActionPastOrPresent = 'Destroy';
			}
			//Since destroyed posts are completely gone we know that if
			// there is an archive Date less that the current date it was archived
		} else {
			archiveActionPastOrPresent = 'Archived';
		}
		return `${archiveActionPastOrPresent} on ${archiveDateShort}`;
	}
}
